import { useRef, useState, useEffect } from "react"
import styled from "styled-components"
import { motion } from "framer-motion"

import ProgressBar from "../../../shared/components/ProgressBar"
import Menu from "../../../shared/components/Menu"
import { EASINGS } from "../../../shared/constants"

const Container = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  display: flex;
  align-items: center;
  pointer-events: none;
`

const StyledVideo = styled(motion.video)`
  width: 100%;
`

const StyledVideoContainer = styled.div`
  width: inherit;
  display: flex;
  flex-direction: column;
  video + div {
    position: sticky;
  }
`

const animations = {
  variants: {
    container: {
      visible: {
        background: "rgba(0,0,0,1)",
        transition: {
          background: {
            duration: 0.5,
            delay: 0.5,
          },
          ease: EASINGS.inSine,
        },
      },
      hidden: {
        background: "rgba(0,0,0,0)",
        transition: {
          background: {
            duration: 0.5,
            delay: 0,
          },
          ease: EASINGS.outSine,
        },
      },
    },
    video: {
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          opacity: {
            duration: 0.5,
            delay: 0.5,
          },
          scale: {
            duration: 1,
            delay: 0.25,
          },
          ease: "easeIn",
        },
      },
      hidden: {
        opacity: 0,
        scale: 1.5,
        transition: {
          opacity: {
            delay: 0,
            duration: 0.5,
          },
          scale: {
            delay: 0,
            duration: 1,
          },
          ease: "easeOut",
        },
      },
    },
  },
  initial: "hidden",
  exit: "hidden",
}

const VideoPlayer = ({ stopVideo, title, videoUri, shallPlayVideo }) => {
  const videoRef = useRef(null)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const video = videoRef.current
    const handleProgress = () => {
      setProgress(video.currentTime / video.duration)
    }

    const handleVideoEnd = () => {
      stopVideo()
    }

    video.addEventListener("timeupdate", handleProgress)
    video.addEventListener("ended", handleVideoEnd)
    return () => {
      video.removeEventListener("timeupdate", handleProgress)
      video.removeEventListener("ended", handleVideoEnd)
    }
  }, [])

  useEffect(() => {
    if (shallPlayVideo) {
      videoRef.current.play()
    }
  }, [shallPlayVideo])

  const onAnimationComplete = (variantName) => {
    if (variantName === "hidden") {
      videoRef.current.currentTime = 0
    }
  }

  return (
    <Container
      variants={animations.variants.container}
      initial={animations.initial}
      exit={animations.exit}
      onAnimationComplete={onAnimationComplete}
      animate={shallPlayVideo ? "visible" : "hidden"}
    >
      <StyledVideoContainer>
        <StyledVideo
          ref={videoRef}
          preload={"auto"}
          src={videoUri}
          variants={animations.variants.video}
          initial={animations.initial}
          animate={shallPlayVideo ? "visible" : "hidden"}
          exit={animations.exit}
        ></StyledVideo>
        <ProgressBar isVisible={Boolean(shallPlayVideo)} progress={progress} />
      </StyledVideoContainer>
      <Menu isVisible={Boolean(shallPlayVideo)} onClose={stopVideo} title={title} hasClose={true} />
    </Container>
  )
}

export default VideoPlayer
